import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';

import { UserAuthHttpService } from '@core/http/user-auth.http.service';

import { DateAdapter } from '@angular/material/core';
import { getLocaleForLang } from '@constants';
import { AppOverlayCleanupService } from '@core/services/app-overlay-cleanup.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import { Language, MFA_ERROR_CODES, User } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from '@core/services/analytics.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { parseError } from '@state/errors.parser';
import * as routerActions from '@state/router/router.actions';
import { SystemSettingsFacade } from '@state/system-settings/system-settings.facade';
import * as userAuthActions from './user-auth.actions';
import { OpenReplayService } from '@core/services/open-replay.service';

@Injectable()
export class UserAuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(UserAuthHttpService);
  private readonly translateService = inject(TranslateService);
  private readonly dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private readonly snackService = inject(StackedMessagesService);
  private readonly ls = inject(LocalStorageService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly openReplayService = inject(OpenReplayService);
  private readonly systemSettingsFacade = inject(SystemSettingsFacade);
  private readonly appOverlayCleanupService = inject(AppOverlayCleanupService);

  activateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.activateAccount),
      switchMap(({ payload }) => {
        return this.http.activateAccount(payload).pipe(
          map(({ emailSent }) => {
            this.analyticsService.trackEvent('user_event', 'user_activated_account');
            return userAuthActions.activateAccountSuccess({ emailSent });
          }),
          catchError(error => {
            const errorMessage = parseError(error, userAuthActions.activateAccount.type);
            return of(userAuthActions.activateAccountError({ errorMessage }));
          })
        );
      })
    )
  );

  activateAccountSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.activateAccountSuccess),
      map(() =>
        routerActions.changeRoute({
          linkParams: ['login'],
          extras: {
            queryParams: {
              message: 'ACTIVATE.ACCOUNT-ACTIVATED-YOU-CAN-NOW-LOGIN-USING-YOUR-PASSWORD',
            },
          },
        })
      )
    )
  );

  login$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userAuthActions.login),
        delay(100),
        switchMap(({ payload, redirect }) => {
          return this.http.loginRequest(payload).pipe(
            map(response => this.mapLoginResponseToAction(response, redirect, userAuthActions.loginMfaRequired)),
            catchError(error => {
              const errorMessage = parseError(error, userAuthActions.login.type);
              return of(userAuthActions.loginError({ errorMessage }));
            })
          );
        })
      ),
    { dispatch: true }
  );

  validateLoginMfa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.validateLoginMfa),
      delay(100),
      switchMap(({ payload, redirect }) => {
        return this.http.validateLoginMfa(payload).pipe(
          map(() => {
            this.analyticsService.trackEvent('user_event', 'user_logged_in');
            return userAuthActions.loginSuccess({ redirect });
          }),
          catchError(error => {
            const errorMessage = parseError(error, userAuthActions.validateLoginMfa.type);

            if (this.shouldResetLoginFlowAfterMfaError(error)) {
              return of(userAuthActions.loginError({ errorMessage }));
            }

            return of(userAuthActions.validateLoginMfaError({ errorMessage }));
          })
        );
      })
    )
  );

  resendLoginMfaCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.resendLoginMfaCode),
      delay(100),
      switchMap(({ payload, redirect }) => {
        return this.http.loginRequest(payload).pipe(
          map(response => this.mapLoginResponseToAction(response, redirect, userAuthActions.resendLoginMfaCodeSuccess)),
          catchError(error => {
            const errorMessage = parseError(error, userAuthActions.resendLoginMfaCode.type);
            return of(userAuthActions.resendLoginMfaCodeError({ errorMessage }));
          })
        );
      })
    )
  );

  getMyself$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.getMyself),
      switchMap(({ redirect }) => {
        return this.http.getMyself().pipe(
          map(user => {
            const userLangInDb: Language = user.language;
            const userLangInLs = this.ls.getUserLang();
            const userLangToUse = userLangInLs ? userLangInLs : userLangInDb;
            this.translateService.use(userLangToUse.key);
            this.ls.setUserLang(userLangToUse);

            // IF User language stored in Local Storage (manipulated by utils buttons)
            // is different than the one in Database
            // then you have to update the one in database immediately at login!

            this.dateAdapter.setLocale(getLocaleForLang(userLangToUse.key));
            this.setOpenReplayUserData(user);

            // Before launching success we have to dispatch loading configuration of modules
            this.systemSettingsFacade.getModulesConfiguration();

            return userAuthActions.getMyselfSuccess({ user });
          }),
          catchError(error => {
            return of(userAuthActions.getMyselfError({ error, redirect }));
          })
        );
      })
    )
  );

  getMyselfError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.getMyselfError),
      map(({ redirect }) => {
        this.closeAllOnLogout();
        return routerActions.changeRoute({ linkParams: ['login'], extras: { queryParams: { redirect } } });
      })
    )
  );

  getBrandingSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.getBrandingSettings),
      switchMap(() => {
        return this.http.getBrandingSettings().pipe(
          map(branding => {
            return userAuthActions.getBrandingSettingsSuccess({ branding });
          }),
          catchError(error => {
            // silent error for now
            return of(userAuthActions.getBrandingSettingsError());
          })
        );
      })
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.logout),
      switchMap(opts => {
        return this.http.logout().pipe(
          map(() => {
            this.analyticsService.trackEvent('user_event', 'user_logged_out');
            return userAuthActions.logoutSuccess(opts);
          }),
          catchError(error => of(userAuthActions.logoutError({ error })))
        );
      })
    )
  );

  logoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.logoutSuccess),
      map(opts => {
        this.closeAllOnLogout();

        if (opts.wasUnauthorized) {
          this.snackService.showError({ message: 'COMMON.ERROR.UNAUTHORIZED-PLEASE-LOGIN' });
        }

        if (opts.sessionTimedOutLogout) {
          return routerActions.changeRoute({ linkParams: ['login'], extras: { queryParams: { safeLogout: true } } });
        }
        return routerActions.changeRoute({ linkParams: ['login'] });
      })
    )
  );

  logoutError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.logoutError),
      map(() => {
        this.closeAllOnLogout();
        return routerActions.changeRoute({ linkParams: ['login'] });
      })
    )
  );

  deleteAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.deleteAccount),
      switchMap(payload => {
        return this.http.deleteAccount(payload.password).pipe(
          map(({ success, message }) => {
            if (success === true) {
              this.closeAllOnLogout();
              this.analyticsService.trackEvent('user_event', 'user_deleted_account');
              return userAuthActions.deleteAccountSuccess();
            }
            return userAuthActions.deleteAccountError({ error: message });
          }),
          catchError(() => of(userAuthActions.deleteAccountError({ error: 'UNKNOWN_ERROR' })))
        );
      })
    )
  );

  deleteAccount$Success$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.deleteAccountSuccess),
      map(() => {
        this.closeAllOnLogout();
        return routerActions.changeRoute({ linkParams: ['account-deleted'] });
      })
    )
  );

  updateMyself$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.updateMyself),
      switchMap(({ payload }) => {
        return this.http.updateMyself(payload).pipe(
          map(updatedUser => {
            this.snackService.showInfo({ message: 'COMMON.NOTIFICATION.DATA_UPDATE_SUCCESS' });
            return userAuthActions.updateMyselfSuccess({ updatedUser });
          }),
          catchError(error => {
            this.snackService.showError({ message: 'COMMON.NOTIFICATION.DATA_UPDATE_ERROR' });
            return of(userAuthActions.updateMyselfError({ error }));
          })
        );
      })
    )
  );

  updateMyLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.updateMyLanguage),
      switchMap(({ payload }) => {
        return this.http.updateMyLanguage(payload).pipe(
          map(updatedUser => userAuthActions.updateMyLanguageSuccess({ updatedUser })),
          catchError(error => of(userAuthActions.updateMyLanguageError({ error })))
        );
      })
    )
  );

  updateMyInvoiceData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.updateMyInvoiceData),
      switchMap(({ invoiceData }) => {
        return this.http.updateMyInvoiceData(invoiceData).pipe(
          map(invoiceData => userAuthActions.updateMyInvoiceDataSuccess({ invoiceData })),
          catchError(() => {
            this.snackService.showError({ message: 'COMMON.ERROR.ERROR_UPDATING_INVOICE_DATA' });
            return of(userAuthActions.updateMyInvoiceDataError());
          })
        );
      })
    )
  );

  updateAdminself$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.updateAdminself),
      switchMap(({ userId, payload }) => {
        return this.http.updateAdminself(userId, payload).pipe(
          map(updatedUser => {
            this.snackService.showInfo({ message: 'COMMON.NOTIFICATION.DATA_UPDATE_SUCCESS' });
            return userAuthActions.updateMyselfSuccess({ updatedUser });
          }),
          catchError(error => {
            this.snackService.showError({ message: 'COMMON.NOTIFICATION.DATA_UPDATE_ERROR' });
            return of(userAuthActions.updateMyselfError({ error }));
          })
        );
      })
    )
  );

  getPasswordResetLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.getPasswordResetLink),
      switchMap(({ email }) => {
        return this.http.getPasswordResetLink(email).pipe(
          map(({ emailResult }) => {
            if (emailResult === true) {
              this.analyticsService.trackEvent('user_event', 'user_sent_password_change_request');
              return userAuthActions.getPasswordResetLinkSuccess({ emailResult });
            }
            return userAuthActions.getPasswordResetLinkError({ errorMessage: 'ERROR_SENDING_RESET_PASSWORD_LINK' });
          }),
          catchError(error => {
            const errorMessage = parseError(error, userAuthActions.getPasswordResetLink.type);
            return of(userAuthActions.getPasswordResetLinkError({ errorMessage }));
          })
        );
      })
    )
  );

  setNewPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userAuthActions.setNewPassword),
      switchMap(({ payload }) => {
        return this.http.setNewPassword(payload).pipe(
          map(({ success }) => userAuthActions.setNewPasswordSuccess({ success })),
          catchError(error => {
            const errorMessage = parseError(error, userAuthActions.setNewPassword.type);
            return of(userAuthActions.setNewPasswordError({ errorMessage }));
          })
        );
      })
    )
  );

  private closeAllOnLogout(): void {
    this.appOverlayCleanupService.closeAll();
  }

  private mapLoginResponseToAction(
    response: { authCompleted: boolean; mfaRequired: boolean; pendingLoginId?: string; maskedPhoneNumber?: string },
    redirect: string | null,
    mfaRequiredAction: typeof userAuthActions.loginMfaRequired | typeof userAuthActions.resendLoginMfaCodeSuccess
  ) {
    if (response.authCompleted) {
      this.analyticsService.trackEvent('user_event', 'user_logged_in');
      return userAuthActions.loginSuccess({ redirect });
    }

    if (response.mfaRequired && response.pendingLoginId && response.maskedPhoneNumber) {
      return mfaRequiredAction({
        pendingLoginId: response.pendingLoginId,
        maskedPhoneNumber: response.maskedPhoneNumber,
      });
    }

    return userAuthActions.loginError({ errorMessage: 'COMMON.ERROR.UNKNOWN-ERROR' });
  }

  private shouldResetLoginFlowAfterMfaError(error: any): boolean {
    const errorCode = this.getErrorCode(error);

    return [
      MFA_ERROR_CODES.challengeAttemptsExceeded,
      MFA_ERROR_CODES.challengeNotFound,
      'UNAUTHORIZED_LOCKED',
    ].includes(errorCode ?? '');
  }

  private getErrorCode(error: any): string | null {
    if (typeof error?.error?.message === 'string') {
      return error.error.message;
    }

    if (typeof error?.error?.code === 'string') {
      return error.error.code;
    }

    if (typeof error?.error === 'string') {
      return error.error;
    }

    return null;
  }

  private setOpenReplayUserData(user: Partial<User>): void {
    // OPEN REPLAY
    // using `?` just to ensure that OpenReplay mechanics won't crash our app
    // OpenReplay IS NOT neccessary for app to work.
    this.openReplayService.setUserID(user?.email ?? '');
    this.openReplayService.setMeta('role', user?.role?.key ?? 'UNSPECIFIED');
    this.openReplayService.setMeta('origin', user?.userOrigin ?? 'INVITED');
  }
}
